import { Observable } from "rxjs"
import { delay, delayWhen, filter, map, switchMap, takeUntil } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { FETCH_TIMESHEET_REPORT_REQUESTED, fetchTimesheetReportRequested } from "../../../../api/actions"
import { getUserAccountId } from "../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    clearTimesheetData,
    CLOCK_IN_OUT_POPOVER_CONFIRM_CLICKED,
    FETCH_TIMESHEET_REPORT_SUCCESS,
    fetchTimesheetReportSuccess,
    TIMESHEET_AGENDA_MOUNTED,
    TIMESHEET_REQUEST_FAILED,
    TIMESHEET_REQUEST_SUCCESS,
    timesheetAgendaMounted,
    TimesheetPopoverClickedParams,
} from "./actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_TIMESHEET_REPORT_DEFAULT_FILTER,
    setTimesheetReportDefaultFilter,
} from "../../../filters/actions"
import { computeRoleSalary, getUserAccountRoles, isTimesheetReportAgendaOpened } from "./selectors"
import { waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { TIME_RECORD_DELETED, TIME_RECORD_UPDATED, timeRecordCreated, timeRecordUpdated } from "../../../data/actions"
import { formatUtcTimestamp } from "../../../../utils/dateFunctions"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { RoleDTO } from "../../employees/positions/types"

export const timesheetAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TIMESHEET_AGENDA_MOUNTED),
        switchMap(() => [clearTimesheetData(), setTimesheetReportDefaultFilter()])
    )

export const redownloadDataAfterSendingEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CLOCK_IN_OUT_POPOVER_CONFIRM_CLICKED, TIME_RECORD_DELETED, TIME_RECORD_UPDATED),
        delayWhen(() => actions$.pipe(ofType(TIMESHEET_REQUEST_SUCCESS))),
        takeUntil(actions$.pipe(ofType(TIMESHEET_REQUEST_FAILED))),
        switchMap((action: Action) => {
            if (!action.payload) {
                return []
            }

            // DELETE ACTION
            if (action.payload.userAccountId) {
                return [fetchTimesheetReportRequested(action.payload.userAccountId)]
            }

            // EDIT ACTION
            if (action.payload.newValue && action.payload.newValue.userAccountId) {
                return [fetchTimesheetReportRequested(action.payload.newValue.userAccountId)]
            }

            // POPOVERS ACTIONS
            if (action.payload.params && action.payload.params.userAccountId) {
                return [fetchTimesheetReportRequested(action.payload.params.userAccountId)]
            }

            return []
        })
    )

export const clockInOutPopoverConfirmClickedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CLOCK_IN_OUT_POPOVER_CONFIRM_CLICKED),
        switchMap((action: Action) => {
            logger.info("clockInOutPopoverConfirmClickedEpic - form submitted", {
                data: action.payload,
            })

            const signedInUser = getUserAccountId(deps.getState())
            const params: TimesheetPopoverClickedParams = action.payload.params
            const roles: RoleDTO[] = action.payload.roles

            const hourlyRate = computeRoleSalary(
                params.roleId,
                roles,
                getUserAccountRoles(deps.getState(), params.userAccountId),
                params.timeRecord
            )

            // Clock Out, close shift
            if (params.timeRecord) {
                return [
                    timeRecordUpdated(
                        {
                            ...params.timeRecord,
                            clockOutTimestamp: formatUtcTimestamp(params.clockOutTimestamp || new Date()),
                            clockOutLocationId: params.clockOutLocationId,
                            clockOutUserAccountId: signedInUser,
                        },
                        params.timeRecord
                    ),
                ]
            }

            // Edit shift
            if (params.editingId) {
                return [
                    timeRecordUpdated(
                        {
                            id: params.editingId,
                            clockInUserAccountId: signedInUser as string,
                            clockOutUserAccountId: params.clockOutTimestamp && signedInUser ? signedInUser : null,
                            clockInTimestamp: formatUtcTimestamp(params.clockInTimestamp || new Date()),
                            clockInLocationId: params.clockInLocationId,
                            clockOutTimestamp: params.clockOutTimestamp
                                ? formatUtcTimestamp(params.clockOutTimestamp)
                                : null,
                            clockOutLocationId: params.clockOutLocationId,
                            roleId: params.roleId as string,
                            hourlyRate: hourlyRate,
                            breaks: params.breaks,
                        },
                        params.timeRecord
                    ),
                ]
            }

            // Clock In / Create shift
            return [
                timeRecordCreated({
                    id: deps.idgen.next(),
                    clockInUserAccountId: signedInUser as string,
                    clockOutUserAccountId: params.clockOutTimestamp && signedInUser ? signedInUser : null,
                    userAccountId: params.userAccountId,
                    clockInTimestamp: formatUtcTimestamp(params.clockInTimestamp || new Date()),
                    clockInLocationId: params.clockInLocationId,
                    clockOutTimestamp: params.clockOutTimestamp ? formatUtcTimestamp(params.clockOutTimestamp) : null,
                    clockOutLocationId: params.clockOutLocationId,
                    roleId: params.roleId as string,
                    hourlyRate: hourlyRate,
                    breaks: params.breaks,
                }),
            ]
        })
    )

export const fetchTimesheetReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isTimesheetReportAgendaOpened(deps.getState())),
        map(() => timesheetAgendaMounted())
    )

export const timesheetDefaultFilterSetEipics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_TIMESHEET_REPORT_DEFAULT_FILTER),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => [clearTimesheetData(), fetchTimesheetReportRequested()])
    )

export const timesheetFilterChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_FILTERS, SET_FILTER_VALUE),
        filter(
            (action: Action) =>
                action.payload.identifier === "timesheet" || action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
        ),
        switchMap(() => [clearTimesheetData(), fetchTimesheetReportRequested()])
    )

// The epics below are hack to trigger loading, which triggers swr timesheets data re-fetch in hooks
// We will get rid of this once we remove timesheets create/update/delete events from epics/rxjs
export const timesheetReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_TIMESHEET_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isTimesheetReportAgendaOpened(deps.getState())),
        map(() => createLoadingStarted("timesheet"))
    )

export const fetchTimesheetReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_TIMESHEET_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isTimesheetReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [fetchTimesheetReportSuccess()]
        })
    )

export const timesheetReportLoadingEndedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_TIMESHEET_REPORT_SUCCESS),
        delay(500),
        map(() => createLoadingEnded("timesheet"))
    )

export default [
    timesheetDefaultFilterSetEipics,
    redownloadDataAfterSendingEpic,
    clockInOutPopoverConfirmClickedEpic,
    timesheetReportLoadingStartedEpics,
    timesheetAgendaMountedEpics,
    fetchTimesheetReportRequestedEpics,
    fetchTimesheetReportOnAccountSwitch,
    timesheetFilterChangedEpics,
    timesheetReportLoadingEndedEpics,
]
